import axios from "axios";
import { getOrFetchXsrfToken } from "newui/features/Auth/utils";
import { getCookie } from "utils/cookiesHelper";
import { showToast } from "utils/helpers";
import { logErrorIfDebug } from "utils/logging";

axios.defaults.withCredentials = true;

// Server URLs
const serverUrlV1 = process.env.REACT_APP_API_URL;
const serverUrlV2 = process.env.REACT_APP_API_URL_V2;

export const XSRF_COOKIE_NAME = "XSRF-TOKEN";

// Create API instances
const apiV1 = axios.create({
	baseURL: serverUrlV1,
	withCredentials: true,
	xsrfCookieName: XSRF_COOKIE_NAME,
	xsrfHeaderName: "X-XSRF-TOKEN",
	timeout: 120000, // Milliseconds
});

const apiV2 = axios.create({
	baseURL: serverUrlV2,
	withCredentials: true,
	timeout: 120000, // Milliseconds
});

// Request interceptor function
const requestInterceptor = (config) => {
	const xsrfToken = getCookie(XSRF_COOKIE_NAME);
	if (xsrfToken) {
		config.headers["X-XSRF-TOKEN"] = xsrfToken;
	}
	return config;
};

// Response interceptor function for API instances
const responseSuccessInterceptor = (response) => {
	const expiresAtHeader = response.headers['x-session-expires-at'];
	if (expiresAtHeader) {
		const expiresAt = Number(expiresAtHeader) * 1000; // convert to ms

		window.__PROCURACON_SESSION_HOOK_UPDATE__ && window.__PROCURACON_SESSION_HOOK_UPDATE__(expiresAt);
	}

	return response;
};

const defaultErrorResponse = (error) => {
	const msg = getErrorMessage(error);
	if (msg) {
		showToast(msg, "Error", false, msg);
	}
	return Promise.reject(error);
}

const xsrfTokenRetry = async (error) => {
	const originalRequest = error.config;
	if (originalRequest._retry) {
		return;
	}
	originalRequest._retry = true;

	try {
		// await the async CSRF token fetch
		await getOrFetchXsrfToken(true);

		// retry original request
		const match = originalRequest.baseURL.toLowerCase().match(/\/api\/(v\d+)/);
		const apiVersion = match ? match[1] : null;

		const retryRequest = requestInterceptor(originalRequest);

		return await (apiVersion === "v1" ? apiV1(retryRequest) : apiV2(retryRequest));
	} catch (csrfError) {
		// fallback: show error and reject
		logErrorIfDebug("CSRF refresh failed", csrfError);
		return defaultErrorResponse(error);
	}
}

const responseErrorInterceptor = async (error) => {

	if (error?.response) {
		switch (error?.response?.status) {
			case 401:
				// we will handle this manually as its when logging in/out
				break;
			case 404:
				showToast(error?.message, "Error");
				break;
			case 403:
				let errorMessage = error?.response?.data?.message;
				if (!errorMessage || errorMessage === "") {
					errorMessage = "This action is unauthorized.";
				}
				showToast(errorMessage, "Error");
				break;
			case 419:
				return xsrfTokenRetry(error);
				break;
			default:
				return defaultErrorResponse(error);
		}
	} else if (error?.message?.includes("timeout")) {
		showToast(
			"The request timed out, please try again.",
			"Error",
			false,
			error?.message
		);
	} else if (error?.code === "ERR_CANCELED") {
		return Promise.reject(error);
	} else {
		showToast(
			`An unexpected error has occurred, please try again. ${error?.message}`,
			"Error",
			false,
			error?.message
		);
	}
	return Promise.reject(error);
};

export const getErrorMessage = (request) => {
	if (!request?.response) {
		return null;
	}

	const data = request.response?.data;
	const errors = data?.errors ?? data?.data?.errors;
	const errorReason = errors?.reason;

	if (errorReason) {
		return errorReason.toString();
	}

	if (data?.title === "Validation Error") {
		let validationMessage = data?.title;
		if (errors) {
			if (typeof errors === "object") {
				const allMessages = [];
				Object.entries(errors).forEach(([validationField, messages]) => {
					if (Array.isArray(messages)) {
						messages.forEach((msg) => {
							allMessages.push(msg);
						});
					} else {
						allMessages.push(messages);
					}
				});

				validationMessage += `: ${allMessages.join(", ")}`;
			}
		}

		return validationMessage;
	}

	return data.message ?? data.error;
};

// Apply interceptors to apiV1
apiV1.interceptors.request.use(requestInterceptor, (error) =>
	Promise.reject(error)
);
apiV1.interceptors.response.use(
	responseSuccessInterceptor,
	responseErrorInterceptor
);

// Apply interceptors to apiV2
apiV2.interceptors.request.use(requestInterceptor, (error) =>
	Promise.reject(error)
);
apiV2.interceptors.response.use(
	responseSuccessInterceptor,
	responseErrorInterceptor
);

// Global axios interceptor for handling auth issues and session timeout
axios.interceptors.response.use(
	(response) => response,
	(error) => {
		const unauthorized = [401, 419];
		if (unauthorized.includes(error.response?.status)) {
			if (!localStorage.getItem("twoFactorRequired")) {
				showToast(
					"Please try and sign in again.",
					"Sorry, your session has expired."
				);

				if (window.location.pathname !== "/") {
					if (window.location.pathname !== "/logout") {
						localStorage.setItem("returnTo", window.location.pathname);
					}
					window.location.href = "/";
				}
			}
		} else if (error.response?.status === 500) {
			showToast("Sorry, there was an error processing your request", "Error");
			return;
		} else {
			return Promise.reject(error);
		}
	}
);

// export const getCsrfCookie = async () => {
//     await axios.get(`${process.env.REACT_APP_API_BASE_URL}/sanctum/csrf-cookie`);
// };

export { apiV1 };
export default apiV2;
